import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { submitInquiry } from "@/lib/api";

const initial = {
  name: "",
  email: "",
  company: "",
  phone: "",
  service: "",
  message: "",
};

export const ContactForm = () => {
  const [values, setValues] = useState(initial);
  const [loading, setLoading] = useState(false);

  const setField = (k) => (e) =>
    setValues((v) => ({ ...v, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!values.name || !values.email || !values.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await submitInquiry(values);
      toast.success("Inquiry received. A partner will reach out within one business day.");
      setValues(initial);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Unable to submit right now. Please try again or email Info@srimansolutions.com";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full bg-[#0B132B]/60 border border-white/10 focus:border-red-500/60 focus:ring-0 focus:outline-none text-white placeholder-slate-500 px-4 py-3.5 text-sm rounded-sm transition-colors";

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="contact-form"
      className="bg-[#0B132B]/40 border border-white/10 p-8 md:p-10"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="overline mb-2 block">Full Name *</label>
          <input
            data-testid="contact-form-name"
            type="text"
            value={values.name}
            onChange={setField("name")}
            placeholder="Jane Doe"
            className={inputClass}
            required
          />
        </div>
        <div>
          <label className="overline mb-2 block">Work Email *</label>
          <input
            data-testid="contact-form-email"
            type="email"
            value={values.email}
            onChange={setField("email")}
            placeholder="jane@company.com"
            className={inputClass}
            required
          />
        </div>
        <div>
          <label className="overline mb-2 block">Company</label>
          <input
            data-testid="contact-form-company"
            type="text"
            value={values.company}
            onChange={setField("company")}
            placeholder="Company Ltd."
            className={inputClass}
          />
        </div>
        <div>
          <label className="overline mb-2 block">Phone</label>
          <input
            data-testid="contact-form-phone"
            type="tel"
            value={values.phone}
            onChange={setField("phone")}
            placeholder="+1 555 000 0000"
            className={inputClass}
          />
        </div>
        <div className="md:col-span-2">
          <label className="overline mb-2 block">Service of interest</label>
          <select
            data-testid="contact-form-service"
            value={values.service}
            onChange={setField("service")}
            className={inputClass}
          >
            <option value="">Select a practice…</option>
            <option value="AML & KYC Compliance Audits">
              AML & KYC Compliance Audits
            </option>
            <option value="AI-Driven Enterprise Sales Engines">
              AI-Driven Enterprise Sales Engines
            </option>
            <option value="Custom IT & Secure Software Engineering">
              Custom IT & Secure Software Engineering
            </option>
            <option value="Business Process Services (BPS)">
              Business Process Services (BPS)
            </option>
            <option value="Employer of Record (EOR) Services">
              Employer of Record (EOR) Services
            </option>
            <option value="GIS Mapping & Geospatial Services">
              GIS Mapping & Geospatial Services
            </option>
            <option value="Full-Cycle Revenue Cycle Management (RCM)">
              Full-Cycle Revenue Cycle Management (RCM)
            </option>
            <option value="Other / Not sure">Other / Not sure</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="overline mb-2 block">
            Tell us about your engagement *
          </label>
          <textarea
            data-testid="contact-form-message"
            value={values.message}
            onChange={setField("message")}
            rows={5}
            placeholder="Scope, timelines, regulatory context, and anything else we should know…"
            className={inputClass}
            required
          />
        </div>
      </div>

      <div className="mt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="text-xs text-slate-500 font-mono tracking-wider">
          Submissions are treated confidentially. NDAs on request.
        </div>
        <button
          type="submit"
          disabled={loading}
          data-testid="contact-form-submit"
          className="group inline-flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 disabled:opacity-70 disabled:cursor-not-allowed text-white font-medium px-7 py-3.5 rounded-sm transition-colors"
        >
          {loading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Submitting…
            </>
          ) : (
            <>
              Send Inquiry
              <ArrowRight
                size={16}
                className="transition-transform group-hover:translate-x-1"
              />
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default ContactForm;
